import { createContext, useContext, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import { fitStage } from './scale.js';
import styles from '../base.module.css';

const ScaleContext = createContext(1);
const NarrowContext = createContext(false);

/** The stage's current scale, for pointer maths on the grid (04 §2.2). */
export function useStageScale(): number { return useContext(ScaleContext); }
/**
 * Whether only the stage's central band is sure to show (a narrow window, such as a phone: `fitStage`), so
 * wide pieces must fit between x 440 and 1000 (owner, 4 October 2026: the Look tray on phones).
 */
export function useNarrowStage(): boolean { return useContext(NarrowContext); }

function subscribeResize(listener: () => void): () => void {
  window.addEventListener('resize', listener);
  return () => window.removeEventListener('resize', listener);
}
const sizeKey = () => `${window.innerWidth}x${window.innerHeight}`;

/** The fixed 1440 × 900 stage, scaled to the window (08 §2.4). */
export function Stage({ children, grain = true }: { readonly children: ReactNode; readonly grain?: boolean }) {
  const key = useSyncExternalStore(subscribeResize, sizeKey, () => '1440x900');
  const [vw, vh] = key.split('x').map(Number) as [number, number];
  const fit = fitStage(vw, vh);
  return <div className={styles.viewport}>
    <div className={styles.sizer} style={{ width: fit.width, height: fit.height, marginTop: fit.marginTop }}>
      <main className={styles.stage} data-v1-stage="" style={{ left: fit.offsetX, transform: fit.scale === 1 ? 'none' : `scale(${fit.scale})` }}>
        {grain && <svg aria-hidden="true" width="0" height="0" style={{ position: 'absolute' }}><defs>
          <filter id="grain" x="0" y="0" width="100%" height="100%">
            <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch" />
            <feColorMatrix type="saturate" values="0" />
          </filter>
        </defs></svg>}
        <ScaleContext.Provider value={fit.scale}><NarrowContext.Provider value={fit.narrow}>{children}</NarrowContext.Provider></ScaleContext.Provider>
      </main>
    </div>
  </div>;
}
